import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { authAPI, aiAPI } from '../api';
import api from '../api';

function MaskedInput({ label, name, placeholder, value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          className="input"
          type={show ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete="off"
        />
        <button type="button" onClick={() => setShow(v => !v)}
          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14 }}>
          {show ? '🙈' : '👁️'}
        </button>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  // X API v2 用 4 トークン
  const [xApi, setXApi] = useState({
    x_api_key: '',
    x_api_secret: '',
    x_access_token: '',
    x_access_secret: '',
  });
  const [anthropicKey, setAnthropicKey] = useState('');
  const [currentSettings, setCurrentSettings] = useState({});
  const [savingX, setSavingX] = useState(false);
  const [verifyingX, setVerifyingX] = useState(false);
  const [savingAi, setSavingAi] = useState(false);

  useEffect(() => {
    authAPI.getSettings().then(r => setCurrentSettings(r.data)).catch(() => {});
  }, []);

  const handleXChange = (e) => setXApi(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSaveX = async () => {
    // 入力されたフィールドだけ送信 (空欄は更新しない)
    const body = Object.fromEntries(
      Object.entries(xApi).filter(([_, v]) => v && v.trim())
    );
    if (Object.keys(body).length === 0) {
      return toast.error('少なくとも 1 つのトークンを入力してください');
    }
    setSavingX(true);
    try {
      await api.post('/auth/settings/xapi', body);
      toast.success('X API トークンを保存しました');
      // 表示用に再取得 + 入力欄をクリア
      const res = await authAPI.getSettings();
      setCurrentSettings(res.data);
      setXApi({ x_api_key: '', x_api_secret: '', x_access_token: '', x_access_secret: '' });
    } catch (e) {
      toast.error(e.response?.data?.error || '保存に失敗しました');
    } finally {
      setSavingX(false);
    }
  };

  const handleVerifyX = async () => {
    setVerifyingX(true);
    try {
      const res = await authAPI.verify();
      const u = res.data.user;
      toast.success(`✅ 接続成功: @${u?.username} (${u?.name || ''})`);
    } catch (e) {
      const msg = e.response?.data?.error || e.message || '認証確認失敗';
      toast.error(msg, { duration: 6000 });
    } finally {
      setVerifyingX(false);
    }
  };

  const handleSaveAI = async () => {
    if (!anthropicKey.trim()) return toast.error('APIキーを入力してください');
    setSavingAi(true);
    try {
      await aiAPI.saveApiKey(anthropicKey.trim());
      toast.success('Anthropic APIキーを保存しました');
      setAnthropicKey('');
    } catch (e) {
      toast.error('保存に失敗しました');
    } finally {
      setSavingAi(false);
    }
  };

  // 各トークンが「設定済み」かどうかを判定
  // (バックエンドは MASK_KEYS に該当する場合 ********、それ以外は値を返すが、
  //  ここでは存在の有無だけ見たいので、key が currentSettings に空文字でなければ「設定済」)
  const isSet = (key) => currentSettings[key] && currentSettings[key] !== '';

  const apiSetCount = ['x_api_key','x_api_secret','x_access_token','x_access_secret'].filter(isSet).length;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">⚙️ 設定</div>
        <div className="page-sub">X API トークン・AI APIキーの登録</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, maxWidth: 900 }}>
        <div className="card">
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>𝕏 X API v2 設定</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 }}>
            X Developer Portal で取得した 4 つのトークンを入力
          </div>

          {apiSetCount > 0 && (
            <div style={{
              background: apiSetCount === 4 ? 'var(--bg-secondary)' : '#fef3c7',
              borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12,
            }}>
              {apiSetCount === 4
                ? <span style={{ color: 'var(--success)' }}>✅ 4 トークンすべて設定済み</span>
                : <span style={{ color: '#b45309' }}>⚠️ {apiSetCount}/4 設定済み (残り {4-apiSetCount} 件)</span>
              }
            </div>
          )}

          <MaskedInput
            label={`API Key (Consumer Key) ${isSet('x_api_key') ? '✓' : ''}`}
            name="x_api_key"
            placeholder={isSet('x_api_key') ? '(設定済み、変更したい場合のみ入力)' : 'API Key'}
            value={xApi.x_api_key} onChange={handleXChange}
          />
          <MaskedInput
            label={`API Secret (Consumer Secret) ${isSet('x_api_secret') ? '✓' : ''}`}
            name="x_api_secret"
            placeholder={isSet('x_api_secret') ? '(設定済み、変更したい場合のみ入力)' : 'API Secret'}
            value={xApi.x_api_secret} onChange={handleXChange}
          />
          <MaskedInput
            label={`Access Token ${isSet('x_access_token') ? '✓' : ''}`}
            name="x_access_token"
            placeholder={isSet('x_access_token') ? '(設定済み、変更したい場合のみ入力)' : 'Access Token'}
            value={xApi.x_access_token} onChange={handleXChange}
          />
          <MaskedInput
            label={`Access Token Secret ${isSet('x_access_secret') ? '✓' : ''}`}
            name="x_access_secret"
            placeholder={isSet('x_access_secret') ? '(設定済み、変更したい場合のみ入力)' : 'Access Token Secret'}
            value={xApi.x_access_secret} onChange={handleXChange}
          />

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={handleSaveX} disabled={savingX} style={{ flex: 1 }}>
              {savingX ? '保存中...' : '💾 保存'}
            </button>
            <button className="btn btn-secondary" onClick={handleVerifyX} disabled={verifyingX || apiSetCount < 4}>
              {verifyingX ? '確認中...' : '🔗 接続確認'}
            </button>
          </div>

          <div style={{ marginTop: 16, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            <strong>取得手順:</strong><br />
            1. <a href="https://developer.x.com/" target="_blank" rel="noopener" style={{ color: 'var(--accent)' }}>developer.x.com</a> で Developer Account 申請<br />
            2. Projects & Apps → + Create App<br />
            3. User authentication settings → Read and write 権限を有効化<br />
            4. Keys and tokens タブから 4 つのトークンを取得
          </div>
        </div>

        <div className="card">
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>🤖 Anthropic API 設定</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 }}>
            AI 生成機能 (ツイート自動生成) に必要
          </div>
          <MaskedInput label="Anthropic API Key" name="anthropic_key" placeholder="sk-ant-..."
            value={anthropicKey} onChange={e => setAnthropicKey(e.target.value)} />
          <button className="btn btn-primary" onClick={handleSaveAI} disabled={savingAi} style={{ width: '100%' }}>
            {savingAi ? '保存中...' : '💾 保存'}
          </button>
          <hr className="divider" />
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>使用モデル</div>
            <span style={{ background: 'var(--purple-dim)', color: 'var(--purple)', padding: '3px 10px', borderRadius: 12, fontSize: 12 }}>
              claude-sonnet-4-20250514
            </span>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 24, maxWidth: 900 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>📌 X API v2 について</div>
        {[
          ['方式', 'X 公式 API (v2) + OAuth 1.0a User Context'],
          ['Free プラン', '月 500 ツイートまで投稿可能 (日 16 件)'],
          ['認証', 'API Key/Secret + Access Token/Secret の 4 トークン (DB 保存)'],
          ['投稿速度', '1-2 秒で完了 (Puppeteer 廃止により大幅高速化)'],
          ['権限', 'App の権限を "Read and write" に設定する必要あり'],
        ].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
            <span style={{ fontWeight: 700, color: 'var(--accent)', minWidth: 80, fontSize: 13 }}>{k}</span>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
